import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Directive, Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import * as i1 from '@slidebean/hydra/dynamic';
import * as i1$1 from '@slidebean/hydra/animator';

class DialogResultResolver {
    constructor() {
        this.promise = new Promise(resolve => {
            this.res = resolve;
        });
    }
    resolve(result) {
        this.res([false, result]);
    }
    reject(reason) {
        this.rej(reason);
    }
    dismiss() {
        this.res([true, void 0]);
    }
}
class DialogContext {
    constructor(resolver, config) {
        this.resolver = resolver;
        this.config = config;
    }
    get data() {
        return this.config.data;
    }
    get isLocked() {
        return this.config.lock;
    }
    get isDismissOnEscapeEnabled() {
        return this.config.dismissOnEscape;
    }
    get hasOverflowEnabled() {
        return this.config.overflow;
    }
    lock() {
        this.config.lock = true;
    }
    unlock() {
        this.config.lock = false;
    }
    enableDismissOnEscape() {
        this.config.dismissOnEscape = true;
    }
    disableDismissOnEscape() {
        this.config.dismissOnEscape = false;
    }
    resolve(result) {
        this.resolver.resolve(result);
    }
    reject(reason) {
        this.resolver.reject(reason);
    }
    dismiss() {
        this.resolver.dismiss();
    }
}
class DialogElement {
}
class DialogRef {
    constructor(resolver, hostRef, dialogRef, onDestroy) {
        this.resolver = resolver;
        this.hostRef = hostRef;
        this.dialogRef = dialogRef;
        this.onDestroy = onDestroy;
        this.result.finally(async () => {
            await this.dialogRef.component.instance.hide();
            this.dialogRef.destroy();
            this.hostRef.destroy();
            this.onDestroy();
        });
    }
    get content() {
        const ref = this.dialogRef;
        return ref.content?.component?.instance;
    }
    get result() {
        return this.resolver.promise;
    }
    close(result) {
        this.resolver.resolve(result);
    }
    dismiss() {
        this.resolver.dismiss();
    }
}
class DialogHostComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogHostComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DialogHostComponent, selector: "hy-dialog-host", ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'hy-dialog-host',
                    template: `<ng-content></ng-content>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });
class DialogPrimitive {
    constructor(context) {
        this.context = context;
    }
    get data() {
        return this.context.data;
    }
    get isLocked() {
        return this.context.isLocked;
    }
    get isDismissOnEscapeEnabled() {
        return this.context.isLocked;
    }
    resolve(result) {
        this.context.resolve(result);
    }
    reject(reason) {
        this.context.reject(reason);
    }
    dismiss() {
        this.context.dismiss();
    }
    lock() {
        this.context.lock();
    }
    unlock() {
        this.context.unlock();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogPrimitive, deps: [{ token: DialogContext }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DialogPrimitive, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: DialogContext }] });
class DialogService {
    constructor(componentFactory, document, factory) {
        this.componentFactory = componentFactory;
        this.document = document;
        this.renderer = factory.createRenderer(void 0, null);
    }
    open(content, config) {
        const resolver = new DialogResultResolver();
        const context = new DialogContext(resolver, Object.assign({}, {
            lock: false,
            dismissOnEscape: true,
            overflow: true,
            data: {},
        }, config));
        const dialog = this.createDialogComponent(context, content);
        const host = this.createHostComponent(dialog);
        this.renderer.appendChild(this.document.body, host.element);
        this.renderer.setAttribute(this.document.body, this.getDialogAttribute(), '');
        return new DialogRef(resolver, host, dialog, this.onDestroy.bind(this));
    }
    onDestroy() {
        this.renderer.removeAttribute(this.document.body, this.getDialogAttribute());
    }
    createHostComponent(dialog) {
        return this.componentFactory.create(DialogHostComponent, {
            content: { value: dialog.element },
        });
    }
    getDialogAttribute() {
        return 'hy-dialog';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogService, deps: [{ token: i1.DynamicComponentFactory }, { token: DOCUMENT }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.DynamicComponentFactory }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.RendererFactory2 }] });

class BackdropPrimitive {
    constructor(animator, element) {
        this.animator = animator;
        this.element = element;
    }
    async ngAfterViewInit() {
        await this.show();
    }
    async show() {
        if (!!this.element)
            await this.animator.stop(this.element.nativeElement);
        if (!this.element)
            return;
        const animation = this.getShowAnimation();
        if (!animation)
            return;
        await this.animator.animate(this.element.nativeElement, animation);
    }
    async hide() {
        if (!this.element)
            return;
        await this.animator.stop(this.element.nativeElement);
        const animation = this.getHideAnimation();
        if (!animation)
            return;
        await this.animator.animate(this.element.nativeElement, animation);
    }
    getShowAnimation() {
        return void 0;
    }
    getHideAnimation() {
        return void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropPrimitive, deps: [{ token: i1$1.Animator }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: BackdropPrimitive, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i1$1.Animator }, { type: i0.ElementRef }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BackdropPrimitive, DialogContext, DialogElement, DialogHostComponent, DialogPrimitive, DialogRef, DialogResultResolver, DialogService };
